import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { OnboardingService } from './onboarding.service';
import { TwoFactorAuthService } from './two-factor-auth.service';
import { AuthService } from './auth.service';
import { UserProfileService } from './user-profile.service';
import { UserOnboardingLevel } from '../store/reducers/session-profile.reducer';
import { InterestWizardService } from './interest-wizard.service';
import { UserInfoService } from './user-info.service';
import { userRoleNewSelector } from '../store/selectors/user-role.selectors';
import {
  userInterestWizardStateSelector,
  userOnboardingLevelSelector,
  userProfileSelector,
} from '../store/selectors/user-profile.selectors';
import { selectOnboardingSecondLevelUrl } from '../store/selectors/onboarding-config.selectors';
import {
  USER_ADDITIONAL_ROLE,
  USER_ROLE,
  verifiedUserRoles,
} from '../store/reducers/session-info.reducer';

@Injectable({
  providedIn: 'root',
})
export class OrchestratorService {
  constructor(
    private onboardingService: OnboardingService,
    private store: Store,
    private twoFactorAuthService: TwoFactorAuthService,
    private authService: AuthService,
    private userProfileService: UserProfileService,
    private interestWizardService: InterestWizardService,
    private userInfoService: UserInfoService,
  ) {}

  private getOnboardingLevelByUserRole(userRole: string[]) {
    if (userRole.some((item: USER_ROLE) => verifiedUserRoles.includes(item))) {
      return 'FULL';
    } else if (userRole.includes(USER_ADDITIONAL_ROLE.PRIVATE_RECORD)) {
      return 'BASIC';
    } else {
      return 'NONE';
    }
  }

  getNextOnboardingStep(): Observable<string | null> {
    return combineLatest([
      this.store.pipe(
        select(userRoleNewSelector),
        filter((x) => !!x),
        take(1),
      ),
      this.store.pipe(
        select(userInterestWizardStateSelector),
        filter((x) => !!x),
        take(1),
      ),
      this.onboardingService.isRecoveryMode(),
      this.onboardingService.hasPendingProcesses(),
    ]).pipe(
      take(1),
      switchMap(
        ([
          userRole,
          interestWizardState,
          isRecoveryMode,
          hasPendingProcesses,
        ]) => {
          if (isRecoveryMode) {
            return of('two-factor/recovery');
          }

          const onboardingLevel = this.getOnboardingLevelByUserRole(userRole);

          if (userRole.some((item) => verifiedUserRoles.includes(item))) {
            // Providers are handled by an external IDP so we do not have to check the 2FA or associated facilities
            return this.authService.enforce2FA$().pipe(
              map((_) => ''),
              catchError((_) => of(null)),
            );
          }

          // Check if the interest wizard was finished
          if (
            onboardingLevel === 'BASIC' &&
            interestWizardState !== 'FINISHED'
          ) {
            if (!interestWizardState || interestWizardState === 'NOT_STARTED') {
              return of('/interests');
            }

            return of(
              this.interestWizardService.getRouteByStepId(interestWizardState),
            );
          }

          if (onboardingLevel === 'NONE') {
            return of('/interests');
          }

          return this.checkNext2FASteps(hasPendingProcesses, onboardingLevel);
        },
      ),
    );
  }

  /**
   * Determines the next 2FA setup url. If 2FA is already set up, it delegates to {@link checkNextFacilitiyAssociationSteps}
   * @param hasPendingProcesses has pending 2FA setup process
   * @param onboardingLevel current onboarding level of user
   * @returns url of the next onboarding step
   */
  private checkNext2FASteps(
    hasPendingProcesses: boolean,
    onboardingLevel: string,
  ) {
    return this.twoFactorAuthService.hasVerifiedDevice().pipe(
      switchMap((hasVerifiedDevice) => {
        return this.twoFactorAuthService.getCurrentProcessInstance().pipe(
          switchMap((currentProcessInstance) => {
            if (hasPendingProcesses || !hasVerifiedDevice) {
              if (
                onboardingLevel === 'BASIC' &&
                currentProcessInstance?.processDefinition ===
                  'onboarding_extended'
              ) {
                return of('/identification');
              } else if (onboardingLevel !== 'FULL') {
                return of('/two-factor/setup');
              }
            }

            return this.checkNextFacilitiyAssociationSteps(onboardingLevel);
          }),
        );
      }),
    );
  }

  /**
   * Checks next step associated with facility association.
   * Only call this method if user has 2FA verified, because request needs elevated scopes that require 2FA.
   */
  private checkNextFacilitiyAssociationSteps(
    onboardingLevel: string,
  ): Observable<string | null> {
    return this.authService.enforce2FA$().pipe(() => {
      return this.store.pipe(
        select(userRoleNewSelector),
        switchMap((userRole) => {
          if (!userRole.some((item) => verifiedUserRoles.includes(item))) {
            return of('/identification');
          }
          if (onboardingLevel === 'FULL') {
            return of('');
          } else {
            return this.store.pipe(selectOnboardingSecondLevelUrl);
          }
        }),
      );
    });
  }

  // Determine if onboarding level updates are necessary
  determineOnboardingLevelUpdate() {
    return combineLatest([
      this.store.pipe(
        select(userOnboardingLevelSelector),
        filter((x) => !!x),
        take(1),
      ),
      this.store.pipe(
        select(userRoleNewSelector),
        filter((x) => !!x),
        take(1),
      ),
    ]).pipe(
      switchMap(([onboardingLevel, userRole]) => {
        if (
          userRole.some((item) =>
            [
              USER_ROLE.HCP,
              USER_ROLE.DPO,
              USER_ROLE.VERIFIED_REPRESENTATIVE,
            ].includes(item),
          )
        ) {
          return of({
            onboardingLevel,
            determinedLevel: 'FULL',
          });
        }
        return this.twoFactorAuthService.hasVerifiedDevice().pipe(
          switchMap((hasVerifiedDevice) => {
            if (hasVerifiedDevice) {
              // check associated facilities only after 2FA is verified because this request needs elevated scopes that require 2FA
              return this.authService.enforce2FA$().pipe(
                map(() => {
                  if (userRole.includes(USER_ROLE.PATIENT)) {
                    return 'FULL';
                  } else {
                    return 'BASIC';
                  }
                }),
                map((determinedLevel) => ({
                  onboardingLevel,
                  determinedLevel,
                })),
              );
            }
            return of({
              onboardingLevel,
              determinedLevel: 'BASIC',
            });
          }),
        );
      }),
      switchMap(
        ({
          onboardingLevel,
          determinedLevel,
        }: {
          onboardingLevel: UserOnboardingLevel;
          determinedLevel: UserOnboardingLevel;
        }) => {
          if (onboardingLevel === determinedLevel) {
            return of(null);
          }
          // Do not downgrade a user. hasVerifiedDevice is a condition to become a full user
          // but we do not want to downgrade users just because they currently have no veified device (e.g. if they lost it)
          if (determinedLevel === 'BASIC' && onboardingLevel === 'FULL') {
            return of(null);
          }
          return of({ onboarding_level: determinedLevel });
        },
      ),
    );
  }

  // Check if profile upddates are necessary and update the profile accordingly
  updateUserProfile(): Observable<any> {
    return combineLatest([this.determineOnboardingLevelUpdate()]).pipe(
      switchMap(([onboardingLevelUpdate]) => {
        let profileUpdates = {};

        if (onboardingLevelUpdate !== null) {
          profileUpdates = {
            ...onboardingLevelUpdate,
          };
        }

        if (Object.keys(profileUpdates).length) {
          return this.userProfileService
            .updateUserProfile$(profileUpdates)
            .pipe(switchMap(() => this.userInfoService.refrehUserInfo()));
        } else {
          return of(null);
        }
      }),
    );
  }

  // Additional onboard validations
  validateOnboardingState(): Observable<boolean> {
    return combineLatest([
      this.store.pipe(
        select(userProfileSelector),
        filter((x) => !!x),
        take(1),
      ),
      this.store.pipe(
        select(userRoleNewSelector),
        filter((x) => !!x),
        take(1),
      ),
    ]).pipe(
      switchMap(([userProfile, userRole]) => {
        if (
          userRole.some((item) =>
            [
              USER_ROLE.HCP,
              USER_ROLE.DPO,
              USER_ROLE.VERIFIED_REPRESENTATIVE,
            ].includes(item),
          )
        ) {
          if (!userProfile.partnerDisplay || !userProfile.partnerCode) {
            return throwError('No partner detected 1');
          }
        }

        return of(true);
      }),
    );
  }
}
